import React, { useState } from "react";
import {
  HiOutlinePencil,
  HiOutlineTrash,
  HiOutlinePlus,
  HiOutlineRefresh,
  HiOutlineLockClosed,
  HiOutlineLogout,
} from "react-icons/hi";
import useLocalProjects from "../hooks/useLocalProjects";
import useAdminAuth from "../hooks/useAdminAuth";
import useScrollReveal, { revealUp } from "../hooks/useScrollReveal";
import "./LocationProjects.css";

const emptyDraft = {
  title: "",
  type: "",
  capacity: "",
  year: "",
  description: "",
  image: "",
};

export default function LocationProjects({ locationId, locationName, seedProjects }) {
  const { projects, addProject, updateProject, deleteProject, resetToDefault, isCustomized } =
    useLocalProjects(locationId, seedProjects);
  const { isAdmin, login, logout } = useAdminAuth();

  const [managing, setManaging] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState(emptyDraft);

  const [showLogin, setShowLogin] = useState(false);
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState("");

  const gridRef = useScrollReveal(({ gsap, el }) => {
    gsap.from(el.querySelectorAll(".location-projects__card"), {
      ...revealUp(null, el),
      stagger: 0.08,
    });
  }, [projects.length]);

  const startAdd = () => {
    setEditingId(null);
    setDraft(emptyDraft);
  };

  const startEdit = (project) => {
    setEditingId(project.id);
    setDraft({
      title: project.title || "",
      type: project.type || "",
      capacity: project.capacity || "",
      year: project.year || "",
      description: project.description || "",
      image: project.image || "",
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setDraft(emptyDraft);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!draft.title.trim()) return;

    if (editingId) {
      updateProject(editingId, draft);
    } else {
      addProject(draft);
    }
    cancelEdit();
  };

  const handleLoginSubmit = (e) => {
    e.preventDefault();
    const ok = login(password);
    if (ok) {
      setShowLogin(false);
      setPassword("");
      setLoginError("");
    } else {
      setLoginError("Incorrect password.");
    }
  };

  const handleLogout = () => {
    logout();
    setManaging(false);
    cancelEdit();
  };

  return (
    <section className="location-projects section">
      <div className="container">
        <div className="location-projects__header">
          <div>
            <span className="eyebrow">Completed Work</span>
            <h2 className="location-projects__title">
              PROJECTS AT <span className="accent-text">{locationName}</span>
            </h2>
          </div>

          <div className="location-projects__admin-controls">
            {isAdmin ? (
              <>
                <button
                  type="button"
                  className="btn btn-outline location-projects__manage-btn"
                  onClick={() => {
                    setManaging((m) => !m);
                    if (!managing) startAdd();
                  }}
                >
                  {managing ? "Done Managing" : "Manage Projects"}
                </button>
                <button
                  type="button"
                  className="location-projects__logout-btn"
                  onClick={handleLogout}
                >
                  <HiOutlineLogout /> Log Out
                </button>
              </>
            ) : (
              <button
                type="button"
                className="location-projects__login-toggle"
                onClick={() => setShowLogin((s) => !s)}
              >
                <HiOutlineLockClosed /> Admin Login
              </button>
            )}
          </div>
        </div>

        {!isAdmin && showLogin && (
          <form className="location-projects__login" onSubmit={handleLoginSubmit}>
            <input
              type="password"
              placeholder="Admin password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                setLoginError("");
              }}
              autoFocus
            />
            <button type="submit" className="btn btn-primary">
              Log In
            </button>
            {loginError && <span className="location-projects__login-error">{loginError}</span>}
          </form>
        )}

        {isAdmin && managing && (
          <div className="location-projects__admin">
            <p className="location-projects__admin-note">
              Changes here are saved in <strong>this browser only</strong> —
              they won't appear for other visitors. Use this to preview
              updates before asking your developer to make them permanent.
            </p>

            <form className="location-projects__form" onSubmit={handleSubmit}>
              <div className="location-projects__form-row">
                <input
                  type="text"
                  placeholder="Project title"
                  value={draft.title}
                  onChange={(e) => setDraft((d) => ({ ...d, title: e.target.value }))}
                  required
                />
                <input
                  type="text"
                  placeholder="Type (e.g. Residential)"
                  value={draft.type}
                  onChange={(e) => setDraft((d) => ({ ...d, type: e.target.value }))}
                />
              </div>
              <div className="location-projects__form-row">
                <input
                  type="text"
                  placeholder="Capacity (e.g. 5 kW)"
                  value={draft.capacity}
                  onChange={(e) => setDraft((d) => ({ ...d, capacity: e.target.value }))}
                />
                <input
                  type="text"
                  placeholder="Year"
                  value={draft.year}
                  onChange={(e) => setDraft((d) => ({ ...d, year: e.target.value }))}
                />
              </div>
              <input
                type="text"
                placeholder="Image URL (optional)"
                value={draft.image}
                onChange={(e) => setDraft((d) => ({ ...d, image: e.target.value }))}
              />
              <textarea
                placeholder="Short description"
                rows={3}
                value={draft.description}
                onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))}
              />
              <div className="location-projects__form-actions">
                <button type="submit" className="btn btn-primary">
                  <HiOutlinePlus /> {editingId ? "Save Changes" : "Add Project"}
                </button>
                {editingId && (
                  <button type="button" className="btn btn-outline" onClick={cancelEdit}>
                    Cancel
                  </button>
                )}
                {isCustomized && (
                  <button
                    type="button"
                    className="location-projects__reset"
                    onClick={resetToDefault}
                  >
                    <HiOutlineRefresh /> Reset to default list
                  </button>
                )}
              </div>
            </form>
          </div>
        )}

        {projects.length === 0 ? (
          <p className="location-projects__empty">
            No projects listed yet{isAdmin && managing ? " — add one above." : "."}
          </p>
        ) : (
          <div className="location-projects__grid" ref={gridRef}>
            {projects.map((project) => (
              <article className="location-projects__card" key={project.id}>
                <div
                  className="location-projects__card-image"
                  style={project.image ? { backgroundImage: `url(${project.image})` } : undefined}
                />
                <div className="location-projects__card-body">
                  <div className="location-projects__card-meta">
                    {project.type && <span>{project.type}</span>}
                    {project.year && <span>{project.year}</span>}
                  </div>
                  <h3>{project.title}</h3>
                  {project.capacity && (
                    <p className="location-projects__card-capacity">{project.capacity}</p>
                  )}
                  {project.description && <p>{project.description}</p>}

                  {isAdmin && managing && (
                    <div className="location-projects__card-actions">
                      <button type="button" onClick={() => startEdit(project)}>
                        <HiOutlinePencil /> Edit
                      </button>
                      <button type="button" onClick={() => deleteProject(project.id)}>
                        <HiOutlineTrash /> Remove
                      </button>
                    </div>
                  )}
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
